import { PlatformUserId } from "./platform/PollyPlatformClient";
import { NudgeMessageType } from "./types";

// Every how many minutes we will check orgs to see if they need sync triggered
export const ORG_SYNC_CHECK_FREQUENCY_MINUTES = 10;

// We will trigger sync on an org if it's more than this many hours
export const ORG_SYNC_FREQUENCY_HOURS = 48;

// Delay before hard delete takes place for deletes from Enterprise Admin Management
export const HARD_DELETE_DELAY_HOURS = 48;
// Meteor cron job frequencies
export const QUEUE_PINGS_CRON_JOB_FREQUENCY_MINUTES = 1; // ping the various queues

export const SEND_POLLYS_CRON_JOB_FREQUENCY_MINUTES = 5; // send out all tracks and scheduled pollys (polls and surveys)
export const POLL_AND_SURVEY_REMINDERS_CRON_JOB_FREQUENCY_MINUTES = 1; // send out survey reminders
export const EXPORTS_CRON_JOB_FREQUENCY_MINUTES = 5; // send out exports
export const CLOSE_POLLS_AND_SURVEYS_CRON_JOB_FREQUENCY_MINUTES = 5; // check polls and surveys which need to be closed
export const PENDING_SQS_MESSAGES_CRON_JOB_FREQUENCY_MINUTES = 5; // send out delayed SQS messages stored in mongo
export const TRIAL_NUDGE_MESSAGES_CRON_JOB_FREQUENCY_MINUTES = 60; // send out trial nudge messages if it's time

// Orphaned Polly Nudge (PS-5517)
export const ORPHANED_POLLY_DM1_DELAY_MINUTES = 5; // DM1 delivered by a delayed SQS message (minute precision)
export const ORPHANED_POLLY_DM2_DELAY_DAYS = 2; // DM2 delivered by the hourly nudge sweep (weekend-deferred)
export const ORPHANED_POLLY_DM_FATIGUE_DAYS = 7; // minimum gap between orphaned-polly campaigns per user

// Common array of all restart trial message types
export const RESTART_TRIAL_MESSAGE_TYPES: NudgeMessageType[] = [
  NudgeMessageType.SEVEN_DAYS_AFTER_TRAIL_RESTART,
  NudgeMessageType.THIRTY_FIVE_DAYS_AFTER_TRAIL_RESTART,
  NudgeMessageType.SIXTY_THREE_DAYS_AFTER_TRAIL_RESTART,
  NudgeMessageType.NINETY_ONE_DAYS_AFTER_TRAIL_RESTART,
];

// Common array of all retention campaign message types
export const RETENTION_MESSAGE_TYPES: NudgeMessageType[] = [
  NudgeMessageType.RETENTION_DM_60_DAYS,
  NudgeMessageType.RETENTION_DM_30_DAYS,
  NudgeMessageType.RETENTION_DM_14_DAYS,
  NudgeMessageType.RETENTION_DM_7_DAYS,
  NudgeMessageType.RETENTION_DM_1_DAY,
];

/**
 * Helper function to check if a nudge message is a retention campaign message
 */
export function isRetentionMessage(messageName: NudgeMessageType): boolean {
  return RETENTION_MESSAGE_TYPES.includes(messageName);
}

// Common array of all Win Back campaign message types
export const WIN_BACK_MESSAGE_TYPES: NudgeMessageType[] = [
  NudgeMessageType.WIN_BACK_DM_30_DAYS,
  NudgeMessageType.WIN_BACK_DM_60_DAYS,
  NudgeMessageType.WIN_BACK_DM_90_DAYS,
  NudgeMessageType.WIN_BACK_DM_120_DAYS,
];

/**
 * Helper function to check if a nudge message is a Win Back campaign message
 */
export function isWinBackMessage(messageName: NudgeMessageType): boolean {
  return WIN_BACK_MESSAGE_TYPES.includes(messageName);
}

// Common array of all Onboarding campaign message types
export const ONBOARDING_MESSAGE_TYPES: NudgeMessageType[] = [
  NudgeMessageType.ONBOARDING_DM_1,
  NudgeMessageType.ONBOARDING_DM_2,
  NudgeMessageType.ONBOARDING_DM_3,
  NudgeMessageType.ONBOARDING_DM_PAIRINGS,
  NudgeMessageType.ONBOARDING_DM_4,
  NudgeMessageType.ONBOARDING_DM_5,
  NudgeMessageType.ONBOARDING_DM_6,
  NudgeMessageType.ONBOARDING_DM_7,
  NudgeMessageType.ONBOARDING_DM_8,
  NudgeMessageType.ONBOARDING_DM_9,
  NudgeMessageType.ONBOARDING_DM_10,
  NudgeMessageType.ONBOARDING_DM_11,
  NudgeMessageType.ONBOARDING_DM_12,
];

/**
 * Helper function to check if a nudge message is an Onboarding campaign message
 */
export function isOnboardingMessage(messageName: NudgeMessageType): boolean {
  return ONBOARDING_MESSAGE_TYPES.includes(messageName);
}

// Common array of all New Org campaign message types
export const NEW_ORG_MESSAGE_TYPES: NudgeMessageType[] = [
  NudgeMessageType.NEW_ORG_DM_1,
  NudgeMessageType.NEW_ORG_DM_PAIRINGS,
  NudgeMessageType.NEW_ORG_DM_2,
  NudgeMessageType.NEW_ORG_DM_3,
  NudgeMessageType.NEW_ORG_DM_4,
  NudgeMessageType.NEW_ORG_DM_5,
  NudgeMessageType.NEW_ORG_DM_6,
  NudgeMessageType.NEW_ORG_DM_7,
  NudgeMessageType.NEW_ORG_DM_8,
];

/**
 * Helper function to check if a nudge message is a New Org campaign message
 */
export function isNewOrgMessage(messageName: NudgeMessageType): boolean {
  return NEW_ORG_MESSAGE_TYPES.includes(messageName);
}

export const NPS_APPEAL_CRON_JOB_FREQUENCY_MINUTES = 15; // send out nps survey appeal to qualitifed users via DMs
export const DM_CAMPAIGN_MESSAGES_CRON_JOB_FREQUENCY_MINUTES = 30;

export const LOG_MEMORY_CRON_JOB_FREQUENCY_MINUTES = 1;

/**
 * System user to represent internal processes not triggered by users for activity logging
 */
export const SYSTEM_USER = "SYSTEM";

/** Default text that shows when a user is leaving a comment on a question */
export const DEFAULT_COMMENT_PROMPT_TEXT = "Please elaborate on your answer";

/** Default time zone */
export const DEFAULT_TIME_ZONE = "America/Los_Angeles";

export const SLACK_MAX_SECTION_TEXT = 3000;
export const SLACK_MAX_FIELD_TEXT = 2000;
export const SLACK_MAX_CONTEXT_TEXT = 255;
export const MAX_RESPONSE_LENGTH = SLACK_MAX_SECTION_TEXT - 100;
export const MAX_QUESTION_OPTION_LENGTH = SLACK_MAX_FIELD_TEXT - 150;
export const MAX_QUESTION_OPTIONS = 92;
export const MIN_QUESTION_TITLE_LENGTH = 3;

export const MAX_SECTION_LENGTH = SLACK_MAX_FIELD_TEXT - 100;
export const MAX_CONTEXT_LENGTH = SLACK_MAX_CONTEXT_TEXT - 50;
/** Audience size considered large enough to require a confirmation for DM sends */
export const LARGE_AUDIENCE_SIZE = 50;
/** Audience size considered large enough to not show live results for single question pollys */
export const DM_POLL_AUDIENCE_LIMIT = 500;
/** Number of author success epehmeral polly tips */
export const MAX_AUTHOR_EPHEMERAL_POLLY_TIP = 6;

export const POLLY_BLUE = "#35A8E0";
export const POLLY_SUCCESS = "#82D9B6";

export const PAIRING_QUICK_START_URL =
  "https://www.polly.ai/help/polly-pairings-quick-start";

/**
 * Max human members (bots excluded) a Pairings channel may have on the free
 * allotment. Mirrored in the platform repo's PairingApiService — keep the two
 * in sync, as with FREE_PAIRING_CHANNELS in queueworker's pairingEntitlement.
 */
export const FREE_PAIRING_MAX_MEMBERS = 24;
/** Lower bound of the near-limit DM band [20, FREE_PAIRING_MAX_MEMBERS]. */
export const PAIRING_NEAR_LIMIT_DM_MIN_MEMBERS = 20;

/**
 * The pairings limit DMs. Their profile.nudgeMessages entries ARE the
 * once-ever guard, so any code replacing that array must carry them across or
 * the DM re-sends.
 */
export function isPairingsLimitNudgeType(
  messageType: NudgeMessageType,
): boolean {
  return (
    messageType === NudgeMessageType.PAIRINGS_FUTURELIMIT_DM_1 ||
    messageType === NudgeMessageType.PAIRINGS_LIMIT_DM_1
  );
}

export const PLATFORM_AUTH_URI: string = process.env["PLATFORM_AUTH_URI"];
export const PLATFORM_API_URI: string = process.env["PLATFORM_API_URI"];

/** Service credentials for Polly Platform */
export const PLATFORM_CLIENT_ID: PlatformUserId = process.env[
  "PLATFORM_CLIENT_ID"
] as PlatformUserId;
export const PLATFORM_CLIENT_SECRET: string =
  process.env["PLATFORM_CLIENT_SECRET"];

export const PLATFORM_VOTING_BASE_URL: string =
  process.env["PLATFORM_VOTING_BASE_URL"];

export const PLATFORM_EMBED_VOTING_BASE_URL: string =
  process.env["PLATFORM_EMBED_VOTING_BASE_URL"];
export const INTEGRATION_TEST_SLACK_URL =
  process.env["INTEGRATION_TEST_SLACK_URL"];

export const DISABLE_REDIS_TLS = process.env["DISABLE_REDIS_TLS"];
export const LEGACY_ENTERPRISE_BRANDING = "Legacy Enterprise";
export const OFFICIAL_MESSAGE_LOG_PREFIX = "[OFFICIAL_MESSAGE]: ";
export const WEB_LOGIN_JWT_SECRET = process.env["WEB_LOGIN_JWT_SECRET"];

export const ALERT_NOTIFICATION_PARAMS =
  process.env["ALERT_NOTIFICATION_PARAMS"];
export const PLATFORM_JWT_KEY = process.env["PLATFORM_JWT_KEY"];

export const ORG_LIST_FOR_ASYNC_GHSHEET_UPDATE = [
  "gXzP4yE5ngGPTGLdp", // Asad's Testing WS Local
  "34D3hPoXSRjHTZSjc", // Asad's Testing WS Beta
  "ih4QwDa7jALpCHxCY", // Asad's Testing WS Staging
  "ELNa57MCZYP6LZmnq", // Asad's Testing WS Prod
  "vageoc35JnBT2irJd", // GS's Polly test workspace local
  "L4Zms8mKsYgr3WS6s", // GS's Polly test workspace beta
  "GDbfiMSvhYJexHDBH", // GS's Polly test workspace staging
  "tdPmxinfZd56nWgPv", // MonteCarlo
  "BB79b6sNZF8zoqzS4", // Scopely Electrum
  "upm9qDjfB4Ja64Pqq", // Scopely Main
  "qFCG65evN3tJytQ82", // Thompson Spencer
  "tkdjmYaHG7Nk5Y9Hq", // Andalite Beta
  "qPFjGmKEcbqmQaHAC", // Andalite Staging
  "9Mx7Zu59K4ePkMpxy", // Andalite Prod
  "aH6Mkjv2KksJ8xAH3", // Veltra
];

export const QNA_GUEST_USER_NAME = "Guest User";
export const POLLY_AI_ORG_ID = process.env["POLLY_AI_ORG_ID"];

// defaults to #admin-dm-testing in Polly AI workspace
export const ADMIN_DM_TEST_CHANNEL_ID =
  process.env["ADMIN_DM_TEST_CHANNEL_ID"] ?? "C06KS5GD1TK";

// defaults to #sales-notify-dm-sent in Polly AI workspace
export const SALES_NOTIFY_DM_SENT_CHANNEL_ID =
  process.env["SALES_NOTIFY_DM_SENT_CHANNEL_ID"] ?? "C06J399J98X";

export const ADMIN_OPS_CUSTOM_DM_BURST = 25;
export const ADMIN_OPS_CUSTOM_DM_PER_SECOND = 2;

export const MONTHS_UNBLOCK_RESULTS_FOR = 3;
export const TRIAL_NEW_DAY_AT_POLLY_CUTTOFF = "2025-02-01";

// For CONFIDENTIAL, filter out segment values with less than 3 participants
export const MIN_SEGMENTATION_USERS = 3;

export const COUPON_ONE_TIME_TO_ANNUAL_UPGRADE_BASIC = "6c4SbKwk";
export const COUPON_ONE_TIME_TO_ANNUAL_UPGRADE_PRO = "3affTsXY";
export const PRICE_ONE_TIME_SUB_UPGRADE_BASIC = 49;
export const PRICE_ONE_TIME_SUB_UPGRADE_PRO = 79;
export const PRICE_ONE_TIME_SUB_UPGRADE = 1;

// Retention campaign discount coupons
export const COUPON_RETENTION_50_OFF = "RENEW50OFF";
export const COUPON_RETENTION_75_OFF = "RENEW75OFF";
export const PERCENT_OFF_ONE_TIME_SUB_UPGRADE_BASIC = 30;
export const PERCENT_OFF_ONE_TIME_SUB_UPGRADE_PRO = 45;

// Team Plan Upsell DM campaign — org size thresholds
// Update these to change which orgs qualify and which variation they see
export const TEAM_PLAN_UPSELL_SMALL_ORG_MAX = 30;
export const TEAM_PLAN_UPSELL_MEDIUM_ORG_MAX = 100;
export const TEAM_PLAN_UPSELL_MAX_BILLABLE_USERS = 200;
export const TEAM_PLAN_UPSELL_MIN_SUB_AGE_DAYS = 30;

export const COUPON_50_OFF_TEAM_PLAN_DM = "50OFFTeamPlanDM";
export const TEAM_PLAN_UPSELL_FIXED_QTY_SMALL_ORG = 24;
export const TEAM_PLAN_PRICE_PER_USER_PER_YEAR = 12;

export const TEAM_PLAN_UPSELL_MESSAGE_TYPES: NudgeMessageType[] = [
  NudgeMessageType.TEAM_PLAN_UPSELL_DM_1,
  NudgeMessageType.TEAM_PLAN_UPSELL_DM_2,
  NudgeMessageType.TEAM_PLAN_UPSELL_DM_3,
];

export function isTeamPlanUpsellMessage(
  messageName: NudgeMessageType,
): boolean {
  return TEAM_PLAN_UPSELL_MESSAGE_TYPES.includes(messageName);
}

// Unassigned License DM campaign
export const UNASSIGNED_LICENSE_MESSAGE_TYPES: NudgeMessageType[] = [
  NudgeMessageType.UNASSIGNED_LICENSE_DM,
];

export function isUnassignedLicenseMessage(
  messageName: NudgeMessageType,
): boolean {
  return UNASSIGNED_LICENSE_MESSAGE_TYPES.includes(messageName);
}

export const UNASSIGNED_LICENSE_MIN_SUB_AGE_DAYS = 30;

export type TeamPlanUpsellOrgSize = "small" | "medium" | "large";

/**
 * Returns the org size bucket for the team plan upsell campaign,
 * or null if the org is too large to qualify.
 */
export function getTeamPlanUpsellOrgSize(
  billableUserCount: number,
): TeamPlanUpsellOrgSize | null {
  if (billableUserCount <= TEAM_PLAN_UPSELL_SMALL_ORG_MAX) {
    return "small";
  }
  if (billableUserCount <= TEAM_PLAN_UPSELL_MEDIUM_ORG_MAX) {
    return "medium";
  }
  if (billableUserCount <= TEAM_PLAN_UPSELL_MAX_BILLABLE_USERS) {
    return "large";
  }
  return null;
}

// Basic-to-PRO Upsell DM campaign
export const BASIC_PRO_UPSELL_MESSAGE_TYPES: NudgeMessageType[] = [
  NudgeMessageType.BASIC_PRO_UPSELL_PLAN_SWITCH,
  NudgeMessageType.BASIC_PRO_UPSELL_DM_1,
  NudgeMessageType.BASIC_PRO_UPSELL_DM_2,
  NudgeMessageType.BASIC_PRO_UPSELL_REVERT,
];

export function isBasicProUpsellMessage(
  messageName: NudgeMessageType,
): boolean {
  return BASIC_PRO_UPSELL_MESSAGE_TYPES.includes(messageName);
}

export const BASIC_PRO_UPSELL_PROMO_DURATION_DAYS = 90;
